// src/axios.js

import axios from "axios";
import API_BASE_URL from "./config";
import {
  isTokenValid,
  clearAuthStorage,
  isSessionActive,
  updateLastActivity,
  shouldRefreshTokenSoon,
} from "./utils/tokenUtils";

// ── الخدمة المحلية لنقطة البيع (http://localhost:5080) تعرّف هذين المتغيرين في index.html
//    فتكلّم الواجهة الخدمة بدل السيرفر، والخدمة تعمل مع الإنترنت وبدونه.
const AGENT = typeof window !== "undefined" && window.__PROFIT_AGENT__ === true;
const BASE_URL = (typeof window !== "undefined" && window.__PROFIT_API_BASE__) || API_BASE_URL;

// توكن الدخول بدون إنترنت (تصدره الخدمة المحلية) — ليس JWT ولا يُجدَّد
const isLocalToken = (t) => typeof t === "string" && t.startsWith("pl.");

const instance = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

// ───────────────────────── الصفحات والطلبات العامة ─────────────────────────

// صفحات لا تحتاج تسجيل دخول — لا تحويل لصفحة الدخول منها
const PUBLIC_PATHS = ["/login", "/register", "/forgot-password", "/reset-password"];

const isPublicPath = () => {
  const p = (window.location.pathname || "/").toLowerCase();
  if (p === "/" || p === "") return true;
  return PUBLIC_PATHS.some(x => p === x || p.startsWith(x + "/"));
};

// طلبات لا تحتاج جلسة — لا فحص للجلسة قبلها ولا تجديد ولا تحويل عند 401
const PUBLIC_ENDPOINTS = [
  "/user/login",
  "/user/refresh-token",
  "/user/logout",
  "/user/forgot-password",
  "/user/reset-password",   // يشمل /user/reset-password/validate
  "/user/check-phone",
  "/auth/register-company",
  "/auth/register",
];

const isPublicEndpoint = (url) => {
  if (!url) return false;
  const u = url.toLowerCase();
  return PUBLIC_ENDPOINTS.some(e => u.includes(e));
};

const forceLogout = () => {
  clearAuthStorage();
  if (!isPublicPath()) window.location.href = "/login";
};

// ───────────────────────── تجديد التوكن (طلب واحد مشترك) ─────────────────────────
// ⚠️ مهم: السيرفر يلغي كل الجلسات إذا استُخدم نفس الـ Refresh Token مرتين،
// لذلك كل محاولات التجديد (الاستباقية وبعد 401) تنتظر نفس الطلب ولا ترسل طلبًا ثانيًا.
let refreshPromise = null;

export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return Promise.reject(new Error("No refresh token"));

  refreshPromise = axios
    .post(`${BASE_URL}/User/refresh-token`, { refreshToken })
    .then(({ data }) => {
      if (!data?.token || !data?.refreshToken) throw new Error("Invalid refresh response");
      localStorage.setItem("token", data.token);
      localStorage.setItem("refreshToken", data.refreshToken);
      return data.token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

// ───────────────────────── قبل إرسال الطلب ─────────────────────────
instance.interceptors.request.use(async (cfg) => {
  const isPublic = cfg._skipAuthCheck || isPublicEndpoint(cfg.url);
  let token = localStorage.getItem("token");

  // ✅ الطلبات العامة: بدون أي فحص للجلسة.
  // نرفق التوكن فقط إن كان صالحًا (المدير يحتاجه عند إضافة مستخدم)،
  // ولا نرفض الطلب بسبب توكن قديم متبقٍ في المتصفح.
  if (isPublic) {
    if (token && isTokenValid(token)) cfg.headers.Authorization = `Bearer ${token}`;
    return cfg;
  }

  if (token) {
    if (!isSessionActive()) {
      forceLogout();
      return Promise.reject(new Error("Session expired due to inactivity"));
    }

    // الخدمة المحلية هي التي تتحقق من الجلسة (صالحة 16 ساعة من الدخول).
    // توكن الدخول بدون إنترنت يُرسل كما هو، وتوكن السيرفر نحاول تجديده
    // فإن فشل (لا يوجد إنترنت) نكمل به ولا نخرج الكاشير وسط الوردية.
    if (AGENT) {
      if (!isLocalToken(token) && (!isTokenValid(token) || shouldRefreshTokenSoon(token))) {
        try {
          token = await refreshAccessToken();
        } catch {
          // بدون إنترنت → الخدمة المحلية تقبل التوكن الحالي
        }
      }
      cfg.headers.Authorization = `Bearer ${token}`;
      updateLastActivity();
      return cfg;
    }

    // التوكن منتهي أو قريب من الانتهاء → جدّده وانتظر قبل الإرسال
    if (!isTokenValid(token) || shouldRefreshTokenSoon(token)) {
      try {
        token = await refreshAccessToken();
      } catch {
        if (!isTokenValid(token)) {
          forceLogout();
          return Promise.reject(new Error("Token expired"));
        }
        // التجديد الاستباقي فشل لكن التوكن ما زال صالحًا → نكمل به
      }
    }

    cfg.headers.Authorization = `Bearer ${token}`;
  }

  updateLastActivity();
  return cfg;
});

// ───────────────────────── بعد الاستجابة ─────────────────────────
instance.interceptors.response.use(
  (response) => {
    if (!response.config._skipAuthCheck && !isPublicEndpoint(response.config.url)) {
      updateLastActivity();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;

    // أخطاء الطلبات العامة تُعاد للشاشة كما هي (مثل: كلمة مرور خاطئة، رابط منتهي)
    if (isPublicEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    // في الخدمة المحلية: 401 تعني أن الخدمة لا تعرف هذه الجلسة → دخول من جديد
    if (AGENT && status === 401 && (isLocalToken(localStorage.getItem("token")) || originalRequest._retry)) {
      forceLogout();
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        forceLogout();
        return Promise.reject(refreshError);
      }
    }

    // 403 = لا يملك صلاحية — لا نخرجه إلا إذا كانت الجلسة نفسها منتهية
    if (status === 403) {
      const t = localStorage.getItem("token");
      if (!t || (!AGENT && !isTokenValid(t)) || !isSessionActive()) {
        forceLogout();
      }
    }

    return Promise.reject(error);
  }
);

export default instance;